import React, { useEffect, useState } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf/dist/jspdf.umd.min';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useHistory } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';
import { toast } from 'react-toastify';
import PageHeader from '../common/PageHeader';
import ActionConfirmModal from '../common/ActionConfirmModal';
import CollapsibleFilterSection from '../common/CollapsibleFilterSection';
import MaskedPhoneInput from '../common/MaskedPhoneInput';
import QuillEditor from '../common/QuillEditor';
import TablePageSizeSelect from '../common/TablePageSizeSelect';
import MedDataTable from '../common/MedDataTable';
import UzDatePicker from '../common/UzDatePicker';
import apiBase from '../../apiConfig';
import useStoredTablePageSize from '../../hooks/useStoredTablePageSize';
import {
  buildAnalysisDocument as buildAnalysisDocumentTemplate,
  buildAnalysisDocumentUrl,
  buildAnalysisQrDataUrl,
  buildAnalysisExportHtml as buildAnalysisExportHtmlTemplate,
  fetchAssetAsDataUrl as fetchAssetAsDataUrlHelper,
  resolveFilialLogoSrc as resolveFilialLogoSrcHelper,
  toFileSafeName as toFileSafeNameHelper
} from './analysisDocumentUtils';

const initialForm = {
  surname: '',
  name: '',
  lastname: '',
  phone: '',
  home_phone: '',
  sex: 0,
  date_birth: '',
  passport: '',
  address: '',
  email: '',
  doctor: '',
  filial_id: '',
  selected_services: []
};

const createInitialForm = () => ({
  ...initialForm,
  selected_services: []
});

const serviceGroups = [
  { key: 'service', title: 'Kabinet xizmatlari', icon: 'stethoscope' },
  { key: 'lab', title: 'Laboratoriya', icon: 'flask' },
  { key: 'oued', title: 'UZI', icon: 'hospital' },
  { key: 'xray', title: 'Rentgen', icon: 'x-ray' }
];

const patientMenuSections = [
  {
    key: 'readyAnalyses',
    label: 'Tayyor analizlar',
    description: "Qarzi yopilgan va natijasi berishga tayyor kartalar.",
    accent: 'primary',
    statLabel: 'tayyor kartalar'
  },
  {
    key: 'envelopePrint',
    label: 'Konvert chop etish',
    description: "Telefon yoki manzil ma'lumoti bor kartalar.",
    accent: 'info',
    statLabel: 'kontaktli yozuvlar'
  },
  {
    key: 'pcrPrint',
    label: 'PCR chop etish',
    description: "Pasport yoki tug'ilgan sana to'ldirilgan kartalar.",
    accent: 'danger',
    statLabel: 'chop uchun tayyor'
  },
  {
    key: 'uzdQueue',
    label: 'UZI navbati',
    description: "Doktor biriktirilgan va yo'nalishi tayyor bemorlar.",
    accent: 'warning',
    statLabel: 'navbat yozuvlari'
  }
];

const getServiceKey = service => `${service.service_type}-${service.service_id}`;

const hasText = value => String(value || '').trim() !== '';
const analysisPreview = value => {
  const plainText = String(value || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (plainText.length <= 160) {
    return plainText || 'Natija hali kiritilmagan';
  }

  return `${plainText.slice(0, 160).trim()}...`;
};

const analysisStatusMeta = {
  pending: { label: 'Kutilmoqda', color: 'soft-secondary' },
  in_progress: { label: 'Jarayonda', color: 'soft-warning' },
  ready: { label: 'Tayyor', color: 'soft-primary' },
  printed: { label: 'Chop etilgan', color: 'soft-success' }
};

const getAnalysisStatusMeta = statusKey => analysisStatusMeta[statusKey] || analysisStatusMeta.pending;

const createAnalysisDrafts = items =>
  items.reduce((acc, item) => {
    acc[item.id] = {
      result: item.result || '',
      status: item.status_key || 'pending',
      template_id: item.template_id ? String(item.template_id) : '',
      method_id: item.method_id ? String(item.method_id) : '',
      norm_ids: Array.isArray(item.norm_ids) ? item.norm_ids.map(id => Number(id)) : []
    };
    return acc;
  }, {});

const patientMatchesSection = (patient, sectionKey) => {
  if (sectionKey === 'readyAnalyses') {
    return Number(patient.debt) <= 0;
  }

  if (sectionKey === 'envelopePrint') {
    return hasText(patient.phone) || hasText(patient.address);
  }

  if (sectionKey === 'pcrPrint') {
    return hasText(patient.passport) || hasText(patient.date_birth);
  }

  if (sectionKey === 'uzdQueue') {
    return hasText(patient.doctor);
  }

  return true;
};

const groupServicesBySection = services =>
  Object.entries(
    services.reduce((acc, item) => {
      const sectionKey = [item.category_name, item.subcategory_name].filter(Boolean).join(' / ') || 'Umumiy';
      if (!acc[sectionKey]) {
        acc[sectionKey] = [];
      }
      acc[sectionKey].push(item);
      return acc;
    }, {})
  ).map(([title, items]) => ({ title, items }));

const getAnalysisMethodTitle = (item, draft) => {
  const selectedMethod = (item.methods || []).find(method => String(method.id) === String(draft.method_id || ''));
  return selectedMethod?.title || item.method_title || "Bo'lim ko'rsatilmagan";
};

const getAnalysisNormLabels = (item, draft) =>
  (item.norm_options || [])
    .filter(norm => (draft.norm_ids || []).includes(Number(norm.id)))
    .map(norm => norm.norm)
    .filter(Boolean);

const Patients = () => {
  const history = useHistory();
  const [patients, setPatients] = useState([]);
  const [filials, setFilials] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [services, setServices] = useState([]);
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useStoredTablePageSize();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(createInitialForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [servicesLocked, setServicesLocked] = useState(false);
  const [activeMenuSection, setActiveMenuSection] = useState('all');
  const [analysisResultsModalOpen, setAnalysisResultsModalOpen] = useState(false);
  const [analysisModalOpen, setAnalysisModalOpen] = useState(false);
  const [analysisPatient, setAnalysisPatient] = useState(null);
  const [analysisItems, setAnalysisItems] = useState([]);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisDrafts, setAnalysisDrafts] = useState({});
  const [analysisSavingId, setAnalysisSavingId] = useState(null);

  const getFilialBrand = patient => filials.find(item => Number(item.id) === Number(patient?.filial_id || 0)) || null;

  const resetAnalysisState = () => {
    setAnalysisPatient(null);
    setAnalysisItems([]);
    setAnalysisLoading(false);
    setAnalysisDrafts({});
    setAnalysisSavingId(null);
  };

  const ensureAnalysisAccess = patient => {
    if (Number(patient?.debt || 0) > 0) {
      toast.warning("Avval bemorning to'lovini yopish kerak");
      return false;
    }

    return true;
  };

  const loadPatientAnalyses = async patient => {
    const res = await axios.get(`${apiBase}/laboratory.php`, {
      params: {
        patient_id: patient.id,
        limit: 500
      }
    });

    if (!res.data?.success) {
      throw new Error("Analiz ma'lumotlari yuklanmadi");
    }

    const items = res.data.control || [];
    setAnalysisItems(items);
    setAnalysisDrafts(createAnalysisDrafts(items));
    return items;
  };

  const openAnalysisResults = async patient => {
    if (!ensureAnalysisAccess(patient)) {
      return;
    }

    setAnalysisPatient(patient);
    setAnalysisItems([]);
    setAnalysisDrafts({});
    setAnalysisResultsModalOpen(true);
    setAnalysisLoading(true);

    try {
      await loadPatientAnalyses(patient);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Analiz ma'lumotlari yuklanmadi");
      setAnalysisResultsModalOpen(false);
      resetAnalysisState();
    } finally {
      setAnalysisLoading(false);
    }
  };

  const openAnalysisDocumentation = async (patient, options = {}) => {
    if (!ensureAnalysisAccess(patient)) {
      return;
    }

    const { reuseLoaded = false } = options;
    setAnalysisPatient(patient);
    setAnalysisModalOpen(true);

    if (reuseLoaded && analysisPatient?.id === patient.id && analysisItems.length > 0) {
      return;
    }

    setAnalysisItems([]);
    setAnalysisDrafts({});
    setAnalysisLoading(true);

    try {
      await loadPatientAnalyses(patient);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Analiz ma'lumotlari yuklanmadi");
      setAnalysisModalOpen(false);
      if (!analysisResultsModalOpen) {
        resetAnalysisState();
      }
    } finally {
      setAnalysisLoading(false);
    }
  };

  const closeAnalysisResultsModal = () => {
    setAnalysisResultsModalOpen(false);
    if (!analysisModalOpen) {
      resetAnalysisState();
    }
  };

  const closeAnalysisModal = () => {
    setAnalysisModalOpen(false);
    if (!analysisResultsModalOpen) {
      resetAnalysisState();
    }
  };

  const openAnalysisDocumentOutput = async (item, mode = 'print') => {
    if (!analysisPatient) {
      return;
    }

    const draft = analysisDrafts[item.id] || {
      result: item.result || '',
      status: item.status_key || 'pending',
      template_id: item.template_id ? String(item.template_id) : '',
      method_id: item.method_id ? String(item.method_id) : '',
      norm_ids: Array.isArray(item.norm_ids) ? item.norm_ids.map(id => Number(id)) : []
    };
    const filial = getFilialBrand(analysisPatient);
    const statusLabel = getAnalysisStatusMeta(draft.status || item.status_key || 'pending').label;
    const documentUrl = buildAnalysisDocumentUrl(analysisPatient.id, item.id);

    try {
      const qrSrc = await buildAnalysisQrDataUrl(documentUrl);

      if (mode === 'pdf') {
        let exportFilial = filial;

        if (filial?.logo) {
          try {
            const logoDataUrl = await fetchAssetAsDataUrlHelper(resolveFilialLogoSrcHelper(filial.logo));
            exportFilial = { ...filial, logo: logoDataUrl || filial.logo };
          } catch (error) {
            exportFilial = { ...filial };
          }
        }

        const payload = buildAnalysisDocumentTemplate({
          patient: analysisPatient,
          item,
          draft,
          filial: exportFilial,
          statusLabel,
          documentUrl,
          qrSrc,
          specialistName: item.specialist_name || '',
          doctorName: analysisPatient.doctor || ''
        });
        const mountNode = document.createElement('div');
        mountNode.style.position = 'fixed';
        mountNode.style.left = '-100000px';
        mountNode.style.top = '0';
        mountNode.style.width = '210mm';
        mountNode.style.zIndex = '-1';
        mountNode.innerHTML = `<style>${payload.styles}</style>${payload.markup}`;
        document.body.appendChild(mountNode);

        try {
          const target = mountNode.querySelector('.med-doc-page');
          if (!target) {
            throw new Error('PDF tayyorlash uchun hujjat topilmadi');
          }

          const canvas = await html2canvas(target, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            windowWidth: target.scrollWidth,
            windowHeight: target.scrollHeight
          });

          const pdf = new jsPDF('p', 'mm', 'a4');
          const imgData = canvas.toDataURL('image/png', 1.0);
          const pageWidth = 210;
          const pageHeight = 297;
          const imgHeight = (canvas.height * pageWidth) / canvas.width;
          let heightLeft = imgHeight;
          let position = 0;

          pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight, undefined, 'FAST');
          heightLeft -= pageHeight;

          while (heightLeft > 0) {
            position = heightLeft - imgHeight;
            pdf.addPage('a4', 'p');
            pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight, undefined, 'FAST');
            heightLeft -= pageHeight;
          }

          pdf.save(`${toFileSafeNameHelper(analysisPatient.full_name)}-${toFileSafeNameHelper(item.analysis_title)}.pdf`);
          toast.success('PDF yuklab olindi');
        } finally {
          document.body.removeChild(mountNode);
        }

        return;
      }

      const printWindow = window.open('', '_blank', 'width=1100,height=820');

      if (!printWindow) {
        toast.error('Chop etish oynasini ochishga ruxsat kerak');
        return;
      }

      printWindow.document.open();
      printWindow.document.write(
        buildAnalysisExportHtmlTemplate({
          patient: analysisPatient,
          item,
          draft,
          filial,
          statusLabel,
          documentUrl,
          qrSrc,
          specialistName: item.specialist_name || '',
          doctorName: analysisPatient.doctor || ''
        })
      );
      printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
      }, 250);
    } catch (error) {
      toast.error(error.message || "Hujjatni tayyorlashda xatolik yuz berdi");
    }
  };

  const updateAnalysisDraft = (id, patch) => {
    setAnalysisDrafts(current => ({
      ...current,
      [id]: {
        ...(current[id] || {}),
        ...patch
      }
    }));
  };

  const handleAnalysisTemplateChange = (item, templateId) => {
    const selectedTemplate = (item.templates || []).find(template => String(template.id) === String(templateId));
    updateAnalysisDraft(item.id, {
      template_id: templateId,
      result: selectedTemplate ? selectedTemplate.text || '' : ''
    });
  };

  const handleAnalysisMethodChange = (itemId, methodId) => {
    updateAnalysisDraft(itemId, {
      method_id: methodId,
      norm_ids: []
    });
  };

  const toggleAnalysisNorm = (itemId, normId) => {
    setAnalysisDrafts(current => {
      const currentDraft = current[itemId] || {};
      const normIds = Array.isArray(currentDraft.norm_ids) ? currentDraft.norm_ids : [];
      const numericNormId = Number(normId);

      return {
        ...current,
        [itemId]: {
          ...currentDraft,
          norm_ids: normIds.includes(numericNormId)
            ? normIds.filter(id => id !== numericNormId)
            : [...normIds, numericNormId]
        }
      };
    });
  };

  const saveAnalysisItem = async item => {
    const draft = analysisDrafts[item.id] || {};
    setAnalysisSavingId(item.id);

    try {
      const res = await axios.put(`${apiBase}/laboratory.php`, {
        entity: 'control',
        id: item.id,
        result: draft.result || '',
        status: draft.status || 'pending',
        template_id: Number(draft.template_id || 0),
        method_id: Number(draft.method_id || 0),
        norm_ids: draft.norm_ids || []
      });

      const updatedItem = res.data?.item;
      if (res.data?.success && updatedItem) {
        setAnalysisItems(current => current.map(entry => (entry.id === updatedItem.id ? updatedItem : entry)));
        setAnalysisDrafts(current => ({
          ...current,
          [updatedItem.id]: {
            result: updatedItem.result || '',
            status: updatedItem.status_key || 'pending',
            template_id: updatedItem.template_id ? String(updatedItem.template_id) : '',
            method_id: updatedItem.method_id ? String(updatedItem.method_id) : '',
            norm_ids: Array.isArray(updatedItem.norm_ids) ? updatedItem.norm_ids.map(id => Number(id)) : []
          }
        }));
        toast.success(res.data.message || 'Analiz saqlandi');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Analiz natijasini saqlashda xatolik yuz berdi');
    } finally {
      setAnalysisSavingId(null);
    }
  };

  const fetchPatients = async () => {
    const [patientsRes, filialsRes, doctorsRes, servicesRes] = await Promise.all([
      axios.get(`${apiBase}/patients.php`, { params: { search, limit: 500 } }),
      axios.get(`${apiBase}/filials.php`),
      axios.get(`${apiBase}/reference.php`, { params: { type: 'doctors' } }),
      axios.get(`${apiBase}/services.php`, { params: { type: 'all' } })
    ]);

    if (patientsRes.data.success) setPatients(patientsRes.data.patients);
    if (filialsRes.data.success) setFilials(filialsRes.data.filials || []);
    if (doctorsRes.data.success) setDoctors(doctorsRes.data.items);
    if (servicesRes.data.success) {
      setServices((servicesRes.data.services || []).filter(item => item.active));
    }
  };

  useEffect(() => {
    fetchPatients().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openCreate = () => {
    setEditingId(null);
    setServicesLocked(false);
    setIsEditLoading(false);
    setForm(createInitialForm());
    setModalOpen(true);
  };

  const openEdit = async patient => {
    setEditingId(patient.id);
    setServicesLocked(false);
    setIsEditLoading(true);
    setForm(createInitialForm());
    setModalOpen(true);

    try {
      const res = await axios.get(`${apiBase}/patients.php`, { params: { id: patient.id } });
      const detail = res.data?.patient;

      if (!res.data?.success || !detail) {
        throw new Error('Bemor ma\'lumotlari yuklanmadi');
      }

      setForm({
        surname: detail.surname || '',
        name: detail.name || '',
        lastname: detail.lastname || '',
        phone: detail.phone || '',
        home_phone: detail.home_phone || '',
        sex: detail.sex || 0,
        date_birth: detail.date_birth || '',
        passport: detail.passport || '',
        address: detail.address || '',
        email: detail.email || '',
        doctor: detail.doctor || '',
        filial_id: detail.filial_id || '',
        selected_services: detail.selected_services || []
      });
      setServicesLocked(Boolean(detail.has_payments));
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Bemor ma\'lumotlari yuklanmadi');
      setModalOpen(false);
      setEditingId(null);
    } finally {
      setIsEditLoading(false);
    }
  };

  const toggleService = service => {
    if (servicesLocked) return;

    const serviceKey = getServiceKey(service);
    const exists = form.selected_services.some(item => getServiceKey(item) === serviceKey);

    setForm({
      ...form,
      selected_services: exists
        ? form.selected_services.filter(item => getServiceKey(item) !== serviceKey)
        : [...form.selected_services, service]
    });
  };

  const toggleServiceSection = sectionServices => {
    if (servicesLocked) return;

    const sectionKeys = sectionServices.map(getServiceKey);
    const allSelected = sectionServices.every(service =>
      form.selected_services.some(selected => getServiceKey(selected) === getServiceKey(service))
    );

    setForm({
      ...form,
      selected_services: allSelected
        ? form.selected_services.filter(item => !sectionKeys.includes(getServiceKey(item)))
        : [
            ...form.selected_services.filter(item => !sectionKeys.includes(getServiceKey(item))),
            ...sectionServices
          ]
    });
  };

  const toggleAllServices = () => {
    if (servicesLocked) return;

    const activeServices = services.filter(item => item.active);
    const shouldReset = form.selected_services.length === activeServices.length;
    setForm({
      ...form,
      selected_services: shouldReset ? [] : activeServices
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!form.doctor) {
      toast.warning('Yangi bemor uchun doktor tanlash zarur');
      return;
    }

    if (!editingId && form.selected_services.length === 0) {
      toast.warning('Kamida bitta laboratoriya yoki boshqa xizmatni belgilang');
      return;
    }

    try {
      if (editingId) {
        const res = await axios.put(`${apiBase}/patients.php`, { ...form, id: editingId });
        if (res.data.success) toast.success(res.data.message);
      } else {
        const res = await axios.post(`${apiBase}/patients.php`, form);
        if (res.data.success) {
          toast.success(res.data.message);
          const patientId = res.data.patient_id;
          if (patientId) {
            history.push(`/payments?patient_id=${patientId}`);
            return;
          }
        }
      }

      setModalOpen(false);
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Bemor saqlanmadi');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await axios.delete(`${apiBase}/patients.php`, { data: { id: deleteTarget.id } });
      if (res.data.success) {
        toast.success(res.data.message);
        fetchPatients();
        setDeleteTarget(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Bemor o‘chirilmadi');
    }
  };

  const sectionStats = patientMenuSections.reduce((acc, section) => {
    acc[section.key] = patients.filter(patient => patientMatchesSection(patient, section.key)).length;
    return acc;
  }, {});

  const filteredPatients =
    activeMenuSection === 'all'
      ? patients
      : patients.filter(patient => patientMatchesSection(patient, activeMenuSection));

  const activeSectionMeta = patientMenuSections.find(section => section.key === activeMenuSection);

  const columns = [
    { dataField: 'id', text: 'ID', sort: true, classes: 'align-middle' },
    { dataField: 'full_name', text: 'F.I.O', sort: true, classes: 'align-middle font-weight-bold' },
    { dataField: 'phone', text: 'Telefon', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    { dataField: 'date_birth', text: "Tug'ilgan sana", sort: true, classes: 'align-middle', formatter: value => value || '-' },
    { dataField: 'filial_name', text: 'Filial', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    {
      dataField: 'debt',
      text: 'Qarz',
      sort: true,
      classes: 'align-middle',
      formatter: value => (
        <Badge color={Number(value) > 0 ? 'soft-danger' : 'soft-success'}>{Number(value || 0).toLocaleString('ru-RU')}</Badge>
      )
    },
    { dataField: 'registrar_name', text: 'Registrator', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    {
      dataField: 'actions',
      text: 'Amallar',
      isDummyField: true,
      headerStyle: { width: '12rem' },
      classes: 'align-middle',
      formatter: (data, item) => (
        <div className="med-actions-row">
          <Button
            color="link"
            className="p-0 med-action-btn med-action-btn-cash"
            title="Kassaga o'tkazish"
            onClick={() => history.push(`/payments?patient_id=${item.id}`)}
          >
            <FontAwesomeIcon icon="cash-register" />
          </Button>
          <Button
            color="link"
            className={`p-0 med-action-btn${Number(item.debt || 0) > 0 ? ' text-400' : ''}`}
            title={
              Number(item.debt || 0) > 0
                ? "To'lov yopilgandan keyin analiz kiritish ochiladi"
                : 'Analiz kiritish'
            }
            onClick={() => openAnalysisDocumentation(item)}
            disabled={Number(item.debt || 0) > 0}
          >
            <FontAwesomeIcon icon="stethoscope" />
          </Button>
          <Button
            color="link"
            className={`p-0 med-action-btn med-action-btn-pdf${Number(item.debt || 0) > 0 ? ' text-400' : ''}`}
            title={
              Number(item.debt || 0) > 0
                ? "To'lov yopilgandan keyin natijalar PDF ko'rinishida ochiladi"
                : 'Natijalarni ko‘rish va PDF/chop qilish'
            }
            onClick={() => openAnalysisResults(item)}
            disabled={Number(item.debt || 0) > 0}
          >
            <FontAwesomeIcon icon="file-pdf" />
          </Button>
          <Button color="link" className="p-0 med-action-btn" onClick={() => openEdit(item)}>
            <FontAwesomeIcon icon="pencil-alt" />
          </Button>
          <Button
            color="link"
            className="p-0 text-danger med-action-btn med-action-btn-danger"
            onClick={() => setDeleteTarget({ id: item.id, name: item.full_name })}
          >
            <FontAwesomeIcon icon="trash-alt" />
          </Button>
        </div>
      )
    }
  ];

  return (
    <>
      <PageHeader
        title="Bemorlar"
        description="Bemorlar registratsiyasi, qidiruv va xizmatlarga biriktirish shu bo'limda boshqariladi."
        className="mb-3"
      />
      <Card className="border-0 shadow-sm med-dashboard-card med-patient-filter-card mb-3">
        <CardBody className="med-dashboard-card-body">
          <CollapsibleFilterSection
            title="Ichki filtrlar"
            summary={`${activeSectionMeta ? activeSectionMeta.label : 'Barcha bemorlar'} holati tanlangan`}
            actions={
              <>
                <Badge color={activeSectionMeta ? `soft-${activeSectionMeta.accent}` : 'soft-primary'} pill>
                  {activeSectionMeta ? activeSectionMeta.label : 'Barcha bemorlar'}: {filteredPatients.length}
                </Badge>
                {activeMenuSection !== 'all' && (
                  <Button
                    type="button"
                    color="light"
                    size="sm"
                    className="med-patient-filter-reset"
                    onClick={() => setActiveMenuSection('all')}
                  >
                    Barchasini ko'rsatish
                  </Button>
                )}
              </>
            }
            storageKey="patients-sections-collapse"
          >
            <div className="med-patient-filter-grid">
              {patientMenuSections.map(section => (
                <button
                  key={section.key}
                  type="button"
                  className={`med-patient-filter-item med-patient-filter-item-${section.accent}${
                    activeMenuSection === section.key ? ' is-active' : ''
                  }`}
                  onClick={() => setActiveMenuSection(section.key)}
                >
                  <span className="med-patient-filter-label">{section.label}</span>
                  <span className="med-patient-filter-meta">
                    <strong>{sectionStats[section.key] || 0}</strong>
                    <small>{section.statLabel}</small>
                  </span>
                </button>
              ))}
            </div>
          </CollapsibleFilterSection>
        </CardBody>
      </Card>
      <Card className="border-0 shadow-sm med-dashboard-card">
        <CardBody className="med-dashboard-card-body">
          <CollapsibleFilterSection
            title="Qidiruv va amallar"
            summary={search ? `Qidiruv: ${search}` : "Barcha bemorlar ro'yxati"}
            storageKey="patients-table-filters-collapse"
          >
            <Row className="mb-3 align-items-center">
              <Col md="auto" className="mb-3 mb-md-0">
                <TablePageSizeSelect value={pageSize} onChange={setPageSize} className="med-table-page-size" />
              </Col>
              <Col md={4}>
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="F.I.O, telefon yoki ID bo'yicha qidirish"
                  className="bg-200 border-0"
                />
              </Col>
              <Col className="text-md-right mt-3 mt-md-0">
                <Button color="outline-primary" className="med-outline-add-btn" onClick={openCreate}>
                  <FontAwesomeIcon icon="plus" className="mr-2" />
                  Yangi bemor
                </Button>
              </Col>
            </Row>
          </CollapsibleFilterSection>
          <MedDataTable
            data={filteredPatients}
            columns={columns}
            pageSize={pageSize}
            paginationResetKey={`${activeMenuSection}-${search}`}
            noDataIndication={
              <div className="med-dashboard-empty">
                Tanlangan ichki bo'lim uchun yozuv topilmadi. Qidiruvni yoki filter holatini o'zgartirib ko'ring.
              </div>
            }
          />
        </CardBody>
      </Card>

      <Modal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        centered
        className="med-patient-modal-dialog"
        contentClassName="med-patient-modal-content"
      >
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
          {editingId ? 'Bemorni tahrirlash' : 'Yangi bemor'}
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody className="med-patient-modal-body">
            {isEditLoading ? (
              <div className="py-5 text-center text-600">
                <FontAwesomeIcon icon="spinner" spin className="mr-2" />
                Bemor ma'lumotlari yuklanmoqda...
              </div>
            ) : (
              <>
            <Row form>
              <Col md={4}>
                <FormGroup>
                  <Label>Familiya</Label>
                  <Input value={form.surname} onChange={e => setForm({ ...form, surname: e.target.value })} />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Ism</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Sharif</Label>
                  <Input value={form.lastname} onChange={e => setForm({ ...form, lastname: e.target.value })} />
                </FormGroup>
              </Col>
            </Row>
            <Row form>
              <Col md={4}>
                <FormGroup>
                  <Label>Telefon</Label>
                  <MaskedPhoneInput value={form.phone} onChange={value => setForm({ ...form, phone: value })} />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Uy telefoni</Label>
                  <MaskedPhoneInput value={form.home_phone} onChange={value => setForm({ ...form, home_phone: value })} />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Tug'ilgan sana</Label>
                  <UzDatePicker value={form.date_birth} onChange={value => setForm({ ...form, date_birth: value })} />
                </FormGroup>
              </Col>
            </Row>
            <Row form>
              <Col md={4}>
                <FormGroup>
                  <Label>Jins</Label>
                  <Input type="select" value={form.sex} onChange={e => setForm({ ...form, sex: e.target.value })}>
                    <option value={0}>Erkak</option>
                    <option value={1}>Ayol</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Passport</Label>
                  <Input value={form.passport} onChange={e => setForm({ ...form, passport: e.target.value })} />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Filial</Label>
                  <Input type="select" value={form.filial_id} onChange={e => setForm({ ...form, filial_id: e.target.value })}>
                    <option value="">Tanlang</option>
                    {filials.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <Label>Email</Label>
                  <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>
                    Birikkan doktor <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    value={form.doctor}
                    required
                    onChange={e => setForm({ ...form, doctor: e.target.value })}
                  >
                    <option value="">Tanlang</option>
                    {doctors.map(item => <option key={item.id} value={item.name}>{item.name}</option>)}
                  </Input>
                  <div className="fs--2 text-500 mt-2">Bemorni saqlashdan oldin mas'ul doktorni tanlang.</div>
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label>Manzil</Label>
              <Input type="textarea" rows="3" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
            </FormGroup>
            <div className="med-service-picker mt-4">
                <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                  <div>
                    <h6 className="mb-1">
                      Xizmatlarni biriktirish {!editingId && <span className="text-danger">*</span>}
                    </h6>
                    <p className="mb-0 text-500 fs--1">
                      {editingId
                        ? 'Bemorga biriktirilgan laboratoriya, kabinet va imaging xizmatlari.'
                        : 'Registratsiya paytida laboratoriya, kabinet va imaging xizmatlarini guruhlar bo‘yicha belgilang.'}
                    </p>
                    {editingId && servicesLocked && (
                      <div className="mt-2 fs--1 text-danger font-weight-semi-bold">
                        Bu bemor bo'yicha to'lov qilingan. Xizmatlarni o'zgartirish bloklangan.
                      </div>
                    )}
                  </div>
                  <div className="d-flex align-items-center mt-3 mt-md-0">
                    <Badge color="soft-primary" pill className="mr-2">
                      {form.selected_services.length} ta tanlandi
                    </Badge>
                    <Button
                      color="light"
                      size="sm"
                      className="med-service-bulk-btn"
                      type="button"
                      onClick={toggleAllServices}
                      disabled={servicesLocked}
                    >
                      <FontAwesomeIcon icon={form.selected_services.length === services.filter(item => item.active).length ? 'times' : 'check'} className="mr-2" />
                      {form.selected_services.length === services.filter(item => item.active).length ? 'Tozalash' : 'Barchasini belgilash'}
                    </Button>
                  </div>
                </div>
                <Row>
                  {serviceGroups.map(group => {
                    const groupItems = services.filter(item => item.service_type === group.key);
                    const sections = groupServicesBySection(groupItems);

                    return (
                      <Col md={6} className="mb-3" key={group.key}>
                        <div className="med-service-group-card">
                          <div className="med-service-group-head">
                            <span className="med-service-group-icon">
                              <FontAwesomeIcon icon={group.icon} />
                            </span>
                            <div>
                              <h6 className="mb-1">{group.title}</h6>
                              <div className="text-500 fs--2">{groupItems.length} ta xizmat</div>
                            </div>
                          </div>
                          <div className="med-service-group-list">
                            {sections.map(section => {
                              const selectedInSection = section.items.filter(item =>
                                form.selected_services.some(selected => getServiceKey(selected) === getServiceKey(item))
                              ).length;

                              return (
                                <div className="med-service-section" key={`${group.key}-${section.title}`}>
                                  <div className="med-service-section-head">
                                    <div>
                                      <div className="font-weight-semi-bold">{section.title}</div>
                                      <div className="text-500 fs--2">
                                        {selectedInSection}/{section.items.length} ta tanlangan
                                      </div>
                                    </div>
                                    <Button
                                      color="link"
                                      type="button"
                                      className="med-service-section-toggle"
                                      onClick={() => toggleServiceSection(section.items)}
                                      disabled={servicesLocked}
                                    >
                                      <FontAwesomeIcon icon={selectedInSection === section.items.length ? 'times' : 'check'} className="mr-1" />
                                      {selectedInSection === section.items.length ? 'Tozalash' : 'Belgilash'}
                                    </Button>
                                  </div>
                                  <div className="med-service-section-items">
                                    {section.items.map(item => {
                                      const checked = form.selected_services.some(
                                        selected => getServiceKey(selected) === getServiceKey(item)
                                      );

                                      return (
                                        <label className={`med-service-option ${checked ? 'is-active' : ''}`} key={getServiceKey(item)}>
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            disabled={servicesLocked}
                                            onChange={() => toggleService(item)}
                                          />
                                          <div className="med-service-option-body">
                                            <div className="font-weight-semi-bold">{item.title}</div>
                                            <div className="text-500 fs--2">
                                              {item.subcategory_name || item.category_name || 'Standart xizmat'}
                                            </div>
                                          </div>
                                          <Badge color="soft-success" pill>
                                            {Number(item.price).toLocaleString('ru-RU')}
                                          </Badge>
                                        </label>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
            </div>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModalOpen(false)}>
              <FontAwesomeIcon icon="times" className="mr-2" />
              Bekor qilish
            </Button>
            <Button color="primary" type="submit" disabled={isEditLoading}>
              <FontAwesomeIcon icon="check-circle" className="mr-2" />
              Saqlash
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <ActionConfirmModal
        isOpen={Boolean(deleteTarget)}
        toggle={() => setDeleteTarget(null)}
        title="Bemorni o'chirish"
        message={
          deleteTarget
            ? `${deleteTarget.name} bo'yicha bog'liq log va to'lovlar ham o'chadi. Amalni tasdiqlaysizmi?`
            : ''
        }
        confirmLabel="O'chirish"
        onConfirm={handleDelete}
      />

      <Modal
        isOpen={analysisResultsModalOpen}
        toggle={closeAnalysisResultsModal}
        size="lg"
        centered
        className="med-confirm-modal"
      >
        <ModalHeader toggle={closeAnalysisResultsModal}>
          {analysisPatient ? `${analysisPatient.full_name} bo'yicha natijalar` : 'Analiz natijalari'}
        </ModalHeader>
        <ModalBody className="med-analysis-doc-modal-body">
          {analysisPatient && (
            <div className="med-analysis-doc-header">
              <div className="med-analysis-doc-header-copy">
                <strong>{analysisPatient.full_name}</strong>
                <span>
                  ID: {analysisPatient.id} | Filial: {analysisPatient.filial_name || '-'} | Telefon: {analysisPatient.phone || '-'}
                </span>
              </div>
              <Badge color="soft-success" pill>
                To'lov yopilgan
              </Badge>
            </div>
          )}

          {analysisLoading ? (
            <div className="text-center py-5 text-500">Analiz natijalari yuklanmoqda...</div>
          ) : analysisItems.length === 0 ? (
            <div className="med-dashboard-empty mt-3">Bu bemor uchun laboratoriya natijalari topilmadi.</div>
          ) : (
            <div className="med-analysis-result-list">
              {analysisItems.map(item => {
                const draft = analysisDrafts[item.id] || {
                  result: item.result || '',
                  status: item.status_key || 'pending',
                  template_id: item.template_id ? String(item.template_id) : '',
                  method_id: item.method_id ? String(item.method_id) : '',
                  norm_ids: Array.isArray(item.norm_ids) ? item.norm_ids.map(id => Number(id)) : []
                };
                const statusMeta = getAnalysisStatusMeta(draft.status || item.status_key || 'pending');
                const normLabels = getAnalysisNormLabels(item, draft);

                return (
                  <div key={`analysis-preview-${item.id}`} className="med-analysis-result-row">
                    <div className="med-analysis-result-main">
                      <div className="med-analysis-result-top">
                        <div>
                          <h6>{item.analysis_title || 'Analiz'}</h6>
                          <p>
                            {getAnalysisMethodTitle(item, draft)} | {item.saved_at || item.date || '-'}
                          </p>
                        </div>
                        <Badge color={statusMeta.color} pill>
                          {statusMeta.label}
                        </Badge>
                      </div>
                      <div className="med-analysis-result-preview">{analysisPreview(draft.result)}</div>
                      <div className="med-analysis-result-meta">
                        <span>Qisqa nom: {item.analysis_short || '-'}</span>
                        {normLabels.length > 0 && <span>Norm: {normLabels.join(', ')}</span>}
                      </div>
                    </div>
                    <div className="med-analysis-result-actions">
                      <Button
                        type="button"
                        color="light"
                        className="med-analysis-result-action"
                        title="Chop etish"
                        onClick={() => openAnalysisDocumentOutput(item, 'print')}
                      >
                        <FontAwesomeIcon icon="print" />
                      </Button>
                      <Button
                        type="button"
                        color="light"
                        className="med-analysis-result-action med-analysis-result-action-pdf"
                        title="PDF"
                        onClick={() => openAnalysisDocumentOutput(item, 'pdf')}
                      >
                        <FontAwesomeIcon icon="file-pdf" />
                      </Button>
                      <Button
                        type="button"
                        color="light"
                        className="med-analysis-result-action"
                        title="Natijani tahrirlash"
                        onClick={() => {
                          setAnalysisResultsModalOpen(false);
                          openAnalysisDocumentation(analysisPatient, { reuseLoaded: true });
                        }}
                      >
                        <FontAwesomeIcon icon="pencil-alt" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeAnalysisResultsModal}>
            Yopish
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={analysisModalOpen} toggle={closeAnalysisModal} size="lg" centered className="med-confirm-modal">
        <ModalHeader toggle={closeAnalysisModal}>
          {analysisPatient ? `${analysisPatient.full_name} bo'yicha analizlar` : 'Analizlar'}
        </ModalHeader>
        <ModalBody className="med-analysis-doc-modal-body">
          {analysisPatient && (
            <div className="med-analysis-doc-header">
              <div className="med-analysis-doc-header-copy">
                <strong>{analysisPatient.full_name}</strong>
                <span>
                  ID: {analysisPatient.id} | Filial: {analysisPatient.filial_name || '-'} | Telefon: {analysisPatient.phone || '-'}
                </span>
              </div>
              <Badge color="soft-success" pill>
                To'lov yopilgan
              </Badge>
            </div>
          )}

          {analysisLoading ? (
            <div className="text-center py-5 text-500">Analizlar yuklanmoqda...</div>
          ) : analysisItems.length === 0 ? (
            <div className="med-dashboard-empty mt-3">Bu bemor uchun laboratoriya yozuvlari topilmadi.</div>
          ) : (
            <div className="med-analysis-doc-list">
              {analysisItems.map(item => {
                const draft = analysisDrafts[item.id] || {
                  result: item.result || '',
                  status: item.status_key || 'pending',
                  template_id: item.template_id ? String(item.template_id) : '',
                  method_id: item.method_id ? String(item.method_id) : '',
                  norm_ids: item.norm_ids || []
                };
                const selectedMethodId = Number(draft.method_id || 0);
                const visibleNorms = (item.norm_options || []).filter(norm => !selectedMethodId || Number(norm.method_id) === selectedMethodId);

                return (
                  <div key={item.id} className="med-analysis-doc-item">
                    <div className="med-analysis-doc-top">
                      <div>
                        <h6>{item.analysis_title || 'Analiz'}</h6>
                        <p>
                          {item.method_title || "Bo'lim ko'rsatilmagan"} | {item.date || '-'}
                        </p>
                      </div>
                      <Badge color={draft.status === 'printed' ? 'soft-success' : draft.status === 'ready' ? 'soft-primary' : draft.status === 'in_progress' ? 'soft-warning' : 'soft-secondary'} pill>
                        {draft.status === 'printed'
                          ? 'Chop etilgan'
                          : draft.status === 'ready'
                            ? 'Tayyor'
                            : draft.status === 'in_progress'
                              ? 'Jarayonda'
                              : 'Kutilmoqda'}
                      </Badge>
                    </div>
                    <div className="med-analysis-doc-meta">
                      <span>Qisqa nom: {item.analysis_short || '-'}</span>
                      <span>Saqlangan vaqt: {item.saved_at || item.date || '-'}</span>
                    </div>

                    {Number(item.result_type_value) !== 1 ? (
                      <>
                        <div className="mt-3">
                          <label className="med-filter-label">Shablon</label>
                          <Input
                            type="select"
                            value={draft.template_id}
                            onChange={event => handleAnalysisTemplateChange(item, event.target.value)}
                            className="bg-200 border-0"
                          >
                            <option value="">Shablon tanlang</option>
                            {(item.templates || []).map(template => (
                              <option key={template.id} value={template.id}>
                                {template.name}
                              </option>
                            ))}
                          </Input>
                        </div>
                        <div className="mt-3">
                          <label className="med-filter-label">Natija matni</label>
                          <QuillEditor
                            value={draft.result || ''}
                            onChange={value => updateAnalysisDraft(item.id, { result: value })}
                            className="med-analysis-doc-editor"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <Row className="mt-3">
                          <Col md={4} className="mb-3">
                            <label className="med-filter-label">Holat</label>
                            <Input
                              type="select"
                              value={draft.status}
                              onChange={event => updateAnalysisDraft(item.id, { status: event.target.value })}
                              className="bg-200 border-0"
                            >
                              <option value="pending">Kutilmoqda</option>
                              <option value="in_progress">Jarayonda</option>
                              <option value="ready">Tayyor</option>
                              <option value="printed">Chop etilgan</option>
                            </Input>
                          </Col>
                          <Col md={4} className="mb-3">
                            <label className="med-filter-label">Metod</label>
                            <Input
                              type="select"
                              value={draft.method_id}
                              onChange={event => handleAnalysisMethodChange(item.id, event.target.value)}
                              className="bg-200 border-0"
                            >
                              <option value="">Tanlang</option>
                              {(item.methods || []).map(method => (
                                <option key={method.id} value={method.id}>
                                  {method.title}
                                </option>
                              ))}
                            </Input>
                          </Col>
                          <Col md={4} className="mb-3">
                            <label className="med-filter-label">Natija</label>
                            {item.has_negative_option ? (
                              <Input
                                type="select"
                                value={draft.result}
                                onChange={event => updateAnalysisDraft(item.id, { result: event.target.value })}
                                className="bg-200 border-0"
                              >
                                <option value="">Tanlang</option>
                                <option value="negative">negative</option>
                                <option value="positive">positive</option>
                                <option value="grey_zone">grey_zone</option>
                              </Input>
                            ) : (
                              <Input
                                value={draft.result}
                                onChange={event => updateAnalysisDraft(item.id, { result: event.target.value })}
                                className="bg-200 border-0"
                                placeholder="Natijani kiriting"
                              />
                            )}
                          </Col>
                        </Row>
                        {visibleNorms.length > 0 && (
                          <div className="mt-2">
                            <label className="med-filter-label d-block">Normlar</label>
                            <div className="med-analysis-doc-norms">
                              {visibleNorms.map(norm => (
                                <label key={`${item.id}-${norm.id}`} className={`med-analysis-doc-norm${draft.norm_ids.includes(Number(norm.id)) ? ' is-active' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={draft.norm_ids.includes(Number(norm.id))}
                                    onChange={() => toggleAnalysisNorm(item.id, norm.id)}
                                  />
                                  <span>{norm.norm}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {Number(item.result_type_value) !== 1 && (
                      <div className="mt-3">
                        <label className="med-filter-label">Holat</label>
                        <Input
                          type="select"
                          value={draft.status}
                          onChange={event => updateAnalysisDraft(item.id, { status: event.target.value })}
                          className="bg-200 border-0"
                        >
                          <option value="pending">Kutilmoqda</option>
                          <option value="in_progress">Jarayonda</option>
                          <option value="ready">Tayyor</option>
                          <option value="printed">Chop etilgan</option>
                        </Input>
                      </div>
                    )}

                    <div className="med-analysis-doc-result">{analysisPreview(draft.result)}</div>
                    <div className="text-right mt-3">
                      <Button color="primary" size="sm" onClick={() => saveAnalysisItem(item)} disabled={analysisSavingId === item.id}>
                        <FontAwesomeIcon icon={analysisSavingId === item.id ? 'spinner' : 'check-circle'} spin={analysisSavingId === item.id} className="mr-2" />
                        Saqlash
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeAnalysisModal}>
            Yopish
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default Patients;
